import { useEffect, useRef, useState } from 'react';
import { Button, Modal, Spin, Typography, message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  openTemplateEditSession,
  saveTemplateEditSession,
  cancelDocumentDraft,
} from '@/services/documentTemplates.service';
import OnlyOfficeEditor from './OnlyOfficeEditor';

const serverMessage = (error) => error?.response?.data?.message;

/**
 * Edits an uploaded Word template inside the app: the Word editor with the
 * placeholder palette on the side (click a placeholder to put it at the
 * cursor). "Save" replaces the template's Word file; "Cancel" discards the
 * working copy and leaves the template as it was.
 *
 * @param {boolean}  open
 * @param {Object}   template - The Word template (`_id`, `name`).
 * @param {Object}   palette  - `{ hint, groups }` shown in the editor.
 * @param {Function} onSaved  - `(savedTemplate) => void`.
 * @param {Function} onClose
 */
export default function TemplateWordEditorModal({ open, template, palette, onSaved, onClose }) {
  const { t } = useTranslation(['forms']);
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);
  // The template a session was requested for in this opening. Effects may run
  // twice (React StrictMode); a second request would open a second working
  // copy, and edits could land in one while "Save" takes the other.
  const requestedForRef = useRef(null);

  const openMutation = useMutation({
    mutationFn: () => openTemplateEditSession(template._id, palette),
    onSuccess: (result) => setSession(result),
    onError: (error) => {
      message.error(
        serverMessage(error) ||
          t('forms:wordEditorFailed', 'Word editor nije mogao da se otvori.')
      );
      onClose?.();
    },
  });

  useEffect(() => {
    if (!open || !template?._id) {
      requestedForRef.current = null;
      return;
    }
    if (requestedForRef.current === template._id) return;
    requestedForRef.current = template._id;
    setSession(null);
    setReady(false);
    openMutation.mutate();
    // One fresh session per opening of the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template?._id]);

  const saveMutation = useMutation({
    mutationFn: () => saveTemplateEditSession(session.draftId),
    onSuccess: (saved) => {
      setSession(null);
      onSaved?.(saved);
      onClose?.();
    },
    onError: (error) =>
      message.error(
        serverMessage(error) ||
          t('forms:templateSaveFailed', 'Čuvanje šablona nije uspelo.')
      ),
  });

  const cancel = () => {
    if (session?.draftId) cancelDocumentDraft(session.draftId).catch(() => {});
    setSession(null);
    onClose?.();
  };

  return (
    <Modal
      open={open}
      title={
        <div>
          <div>{t('forms:wordTemplateEditorTitle', 'Uređivanje Word šablona')}</div>
          <Typography.Text type="secondary" className="!text-sm !font-normal">
            {template?.name}
          </Typography.Text>
        </div>
      }
      width="min(1300px, 96vw)"
      style={{ top: 16 }}
      maskClosable={false}
      keyboard={false}
      destroyOnHidden
      onCancel={cancel}
      footer={
        <div className="flex items-center justify-between gap-2">
          <Typography.Text type="secondary" className="text-left text-xs">
            {t(
              'forms:wordTemplateEditorHint',
              'Postavite kursor u dokument i kliknite oznaku levo. Izmene se čuvaju u šablon tek na „Sačuvaj".'
            )}
          </Typography.Text>
          <div className="flex shrink-0 gap-2">
            <Button
              className="btn-default btn-default--hover"
              onClick={cancel}
              disabled={saveMutation.isPending}
            >
              {t('forms:cancel', 'Otkaži')}
            </Button>
            <Button
              type="primary"
              className="btn-primary"
              loading={saveMutation.isPending}
              disabled={!ready}
              onClick={() => saveMutation.mutate()}
            >
              {t('forms:save', 'Sačuvaj')}
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ height: 'calc(100vh - 230px)', minHeight: 420 }}>
        {session ? (
          <OnlyOfficeEditor
            documentServerUrl={session.editor?.documentServerUrl}
            config={session.editor?.config}
            onReady={() => setReady(true)}
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <Spin />
          </div>
        )}
      </div>
    </Modal>
  );
}
